import { camelCase, keyBy } from 'lodash';
import { shallowEqual, useSelector } from 'react-redux';
import {
  Field,
  FieldOption,
  FieldValue,
  FieldValueType,
  StatusField,
  StatusFieldOption,
} from 'api/tasks';
import { FieldType, SourceType } from 'components/organisms/tasks/fields';
import { useSprintFieldsQuery } from 'queries';
import { byFieldName } from 'utils/by-field-name';
import { RootState } from '../../index';

const selectFields = ({ taskFields }: RootState): AccountFields => taskFields;

export interface AccountFields {
  accountId: number | null;
  accountName: string | null;
  objectType: string | null;
  fields: Array<Field>;
  projectPhaseField: {
    fields: Field[];
  };
}

/**
 *
 * @deprecated Use `FieldsStore` instead
 */
const useAccountFields = (): AccountFields =>
  useSelector(selectFields, shallowEqual);

export interface TaskFieldsByProject {
  defaultFields: Array<Field>;
  projectFields: Array<Field>;
}

/**
 *
 * @deprecated Use `FieldsStore.getTaskFieldByName` instead
 */
export const useAccountField = (fieldName: string): Field | undefined => {
  const { fields } = useAccountFields();

  return fields.find((f) => byFieldName(f, fieldName));
};

export const PRIORITY_FIELD_NAME = 'priority';

/**
 *
 * @deprecated Use `FieldsStore.getTaskFieldByName` instead
 */
export const usePriorityField = (): Field => {
  const field = useAccountField(PRIORITY_FIELD_NAME);

  if (!field) {
    throw new Error('Priority Field is missing');
  }

  return field;
};

const EFFORT_FIELD_NAME = 'effort';

/**
 *
 * @deprecated Use `FieldsStore.getTaskFieldByName` instead
 */
export const useEffortField = (): Field | undefined =>
  useAccountField(EFFORT_FIELD_NAME);

/**
 *
 * @deprecated Use `FieldsStore.getTaskFieldByName` instead
 */
export const usePriorityFieldOption = (
  value: number,
): FieldOption<number> | undefined => {
  const priority = usePriorityField();

  return (
    priority &&
    (priority?.metaData?.options ?? []).find((o) => o.value === value)
  );
};

/**
 *
 * @deprecated Use `FieldsStore.getTaskFieldByName` instead
 * This is not reusable
 */
export const useSelectedPriority = (
  fields: FieldValue<FieldValueType>[],
): FieldOption<number> | undefined => {
  const priority = usePriorityField();
  const field = fields.find(
    (f) => f.fieldId === priority?.fieldId,
  ) as FieldValue<number>;

  return usePriorityFieldOption(field?.fieldValue);
};

export const STATUS_FIELD_NAME = 'Status';

/**
 *
 * @deprecated Use `FieldsStore.getTaskFieldByName` instead
 */
export const useStatusField = (): StatusField => {
  const field = useAccountField(STATUS_FIELD_NAME);

  if (!field) {
    throw new Error('Status Field is missing ');
  }

  return field as StatusField;
};

/**
 *
 * @deprecated Use `FieldsStore.getTaskFieldByName` instead
 */
export const useSelectedStatusOption = (
  fields?: FieldValue<FieldValueType>[],
): StatusFieldOption | undefined => {
  const status = useStatusField();

  if (!fields) {
    return undefined;
  }
  const field = fields.find(
    (f) => f.fieldId === status.fieldId,
  ) as FieldValue<number>;
  const selectedOption =
    field && status.metaData?.options.find((o) => o.value === field.fieldValue);

  return selectedOption && (selectedOption as StatusFieldOption);
};

/**
 *
 * @deprecated Use `FieldsStore` instead with a lookup by type and name
 */
const useProjectPhaseFields = (): Field[] => {
  const { projectPhaseField } = useAccountFields();

  return projectPhaseField.fields;
};

/**
 *
 * @deprecated Use `FieldsStore` instead with a lookup by type and name
 */
export const useProjectPhaseStatusField = (): StatusField => {
  const projectPhaseFields = useProjectPhaseFields();
  const lookup = keyBy(projectPhaseFields, 'fieldName');

  return lookup.Status as StatusField;
};

type SprintStatustype = StatusField & {
  type: FieldType;
  sourceType: SourceType;
};

export const useSprintStatusField = (): SprintStatustype => {
  const { data: sprintfields } = useSprintFieldsQuery();
  const lookup = keyBy(sprintfields, 'fieldName');

  return {
    ...lookup.Status,
    type: FieldType.SingleSelect,
    sourceType: SourceType.Sprint,
    fieldName: camelCase(lookup.Status?.fieldName),
  } as SprintStatustype;
};
